"use client";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import type { BlogPost } from "@/lib/contentful/blog";
import type { Options } from "@contentful/rich-text-react-renderer";

interface BlogContentProps {
  post: BlogPost;
}

export default function BlogContent({ post }: BlogContentProps) {
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderOptions: Options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const asset = post.bodyCopy.links.assets.block.find(
          (asset) => asset.sys.id === node.data.target.sys.id,
        );
        if (!asset) return null;

        return (
          <div className="my-8">
            {asset.url.includes(".mp4") ? (
              <video controls className="w-full rounded-xl">
                <source src={asset.url} type="video/mp4" />
              </video>
            ) : (
              <Image
                src={asset.url}
                alt={asset.title || ""}
                width={asset.width}
                height={asset.height}
                className="w-full rounded-xl"
              />
            )}
          </div>
        );
      },
    },
  };

  return (
    <article>
      <div className="container mx-auto px-4">
        {/* Cover Image */}
        <div className="relative aspect-[32/9] w-full lg:rounded-xl overflow-hidden mb-12">
          <Image
            src={post.coverImage.url}
            alt={post.title}
            fill
            className="object-cover"
            priority
          />
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto">
          <div className="text-text-muted-light dark:text-text-muted-dark">
            {formatDate(post.publishedDate)}
          </div>

          <h1 className="text-4xl md:text-5xl font-medium mt-4 mb-8">
            {post.title}
          </h1>

          {/* Author */}
          {post.author && (
            <Link
              href={`/team/${post.author.slug}`}
              className="flex items-center gap-4 mb-12"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="relative w-16 h-16 rounded-full overflow-hidden"
              >
                <Image
                  src={`/assets/team/${post.author.slug}.webp`}
                  alt={post.author.title}
                  fill
                  className="object-cover"
                />
              </motion.div>
              <div>
                <div className="flex items-center gap-2">
                  <span>by {post.author.title}</span>
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-dark/5 dark:bg-white/5">
                    {post.author.emoji}
                  </span>
                </div>
                <div className="text-text-muted-light dark:text-text-muted-dark">
                  {post.author.position}
                </div>
              </div>
            </Link>
          )}

          {/* Body Content */}
          <div className="prose dark:prose-invert max-w-none text-lg">
            {documentToReactComponents(post.bodyCopy.json, renderOptions)}
          </div>
        </div>
      </div>
    </article>
  );
}
